import React, { useState, useEffect } from "react";
import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

function Example() {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filteredOtherData, setFilteredOtherData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Added for year filter
  let totalProfit = 0; // Initialize totalProfit outside the function
  const monthNames = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];
  const currentMonthShortForm = () => {
    const monthIndex = new Date().getMonth();
    return monthNames[monthIndex];
  };

  const [selectedMonth, setSelectedMonth] = useState(currentMonthShortForm());

  const monthToNumber = (month) => {
    const months = {
      JAN: 1,
      FEB: 2,
      MAR: 3,
      APR: 4,
      MAY: 5,
      JUN: 6,
      JUL: 7,
      AUG: 8,
      SEP: 9,
      OCT: 10,
      NOV: 11,
      DEC: 12,
    };
    return months[month] || 0;
  };

  const parseMonthFromOrderNumber = (orderNumber) => {
    const monthPart = orderNumber.startsWith("OP")
      ? orderNumber.slice(2, 5)
      : orderNumber.slice(0, 3);
    return monthPart.toUpperCase();
  };

  const sortByOrderNumber = (data) => {
    return data.sort((a, b) => {
      const [aMonth, aNumber] = [
        parseMonthFromOrderNumber(a.OrderNumber),
        parseInt(a.OrderNumber.slice(-2)),
      ];
      const [bMonth, bNumber] = [
        parseMonthFromOrderNumber(b.OrderNumber),
        parseInt(b.OrderNumber.slice(-2)),
      ];
      const aMonthNum = monthToNumber(aMonth);
      const bMonthNum = monthToNumber(bMonth);
      return aMonthNum !== bMonthNum
        ? bMonthNum - aMonthNum
        : bNumber - aNumber;
    });
  };

  const expense = 15000 + 21000 + 21000 + 40000 + 5000 + 6500 + 600;
  let wholeSale = 0;
  const groupDataByAllMonths = (data, selectedYear) => {
    const months = [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ];

    const result = months.map((month) => ({
      Month: month,
      Count: 0,
      Sold: 0,
      Remaining: 0,
      LostPhones: 0,
      LostPhones$: 0,
      COGS: 0,
      GrossProfit: 0,
      Sales: 0,
      Expense: 0,
      NetProfit: 0,
      AverageRate: 0,
      RateCount: 0,
      TotalRate: 0,
    }));

    const monthFromDate = (timestamp) => {
      return months[new Date(timestamp).getMonth()];
    };

    const filteredByYear = data.filter(
      (item) =>
        new Date(item.TimeStamp).getFullYear() === parseInt(selectedYear, 10)
    );

    filteredByYear.forEach((item) => {
      const month = monthFromDate(item.TimeStamp);
      const index = months.indexOf(month);
      if (index === -1) return;

      const row = result[index];
      row.Count += 1;

      if (item.ourStat === "Sold") row.Sold += 1;
      if (item.ourStat === "Received") row.Remaining += 1;
      if (["Custom", "Lost", "Returned"].includes(item.ourStat)) {
        row.LostPhones += 1;
        row.LostPhones$ += Number(item.Price) || 0;
      }

      row.Sales += Number(item.soldPrice) || 0;
      row.COGS += Number(item.Price) || 0;
      wholeSale +=
        (Number(item.Price) +
          Number(item.com1) +
          Number(item.com2) +
          Number(item.deliveryFee)) *
          Number(item.rate) || 0 + Number(item.carrier);
      row.GrossProfit +=
        Number(item.soldPrice) -
        ((Number(item.Price) +
          Number(item.com1) +
          Number(item.com2) +
          Number(item.deliveryFee)) *
          Number(item.rate) || 0 + Number(item.carrier));
          
      if (item.rate) {
        row.TotalRate += item.rate;
        row.RateCount += 1;
      }
    });

    result.forEach((row) => {
      if (row.RateCount > 0) {
        row.AverageRate =
          Math.round((row.TotalRate / row.RateCount) * 100) / 100;
      }
    });

    return result;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/items`);
      const fetchedData = response.data.map((item) => ({
        ...item,
        imageUrl1: `${apiUrl}/uploads/${item.ImageFilename1}`,
        imageUrl2: `${apiUrl}/uploads/${item.ImageFilename2}`,
      }));
      const sortedData = sortByOrderNumber(fetchedData);

      setData(sortedData);
      setFilteredData(groupDataByAllMonths(sortedData, selectedYear));
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrors(error.response?.data?.message || "An error occurred");
    }
  };

  const fetchDataOther = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/otherItems`);
      const fetchedData = response.data.map((item) => ({
        ...item,
        imageUrl1: `${apiUrl}/uploads/${item.ImageFilename1}`,
        imageUrl2: `${apiUrl}/uploads/${item.ImageFilename2}`,
      }));
      const sortedData = sortByOrderNumber(fetchedData);
      setFilteredOtherData(groupDataByAllMonths(sortedData)); // Initialize with current month data
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrors(error.response?.data?.message || "An error occurred");
    }
  };

  const parseYearFromOrderNumber = (orderNumber) => {
    const match = orderNumber.match(/(\d{4})$/);
    return match ? match[1] : null;
  };

  useEffect(() => {
    fetchData();
  }, [selectedYear, data]);

  const filterDataByYear = (data, year) => {
    return data.filter((item) => {
      const itemYear = new Date(item.TimeStamp).getFullYear();
      return itemYear === parseInt(year, 10);
    });
  };

  const applyFilters = (data, year) => {
    let filtered = filterDataByYear(data, year);
    return filtered;
  };

  const filterByYear = (year = new Date().getFullYear()) => {
    setSelectedYear(year);
    const filtered = applyFilters(data, year);
    setFilteredData(filtered);
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    filterByYear(year);
  };

  return (
    <div className="flex flex-col self-center content-center w-3/4 max-h-screen mb-20 border-1 border-slate-200 rounded-lg">
      <div className="my-2 flex flex-row justify-between items-center p-5">
        <div className="flex flex-row">
          <label htmlFor="year-select" className="font-bold text-lg mr-2">
            Year:
          </label>
          <input
            id="year-select"
            type="number"
            value={selectedYear}
            onChange={handleYearChange}
            min="2000"
            max={new Date().getFullYear()}
          />
        </div>
        <h1 className="font-bold text-2xl">Overall</h1>
        <h1 className="font-bold text-2xl"></h1>
      </div>
      <div className="flex justify-center w-full overflow-y-auto custom-scrollbar p-5">
        <table className="w-full border-collapse">
          <thead className="sticky top-0">
            <tr>
              <th className="sticky top-0 z-10 pb-3 border-b border-slate-600 text-left  bg-gray-200">
                Month
              </th>
              <th className="sticky top-0 z-10 pb-3 border-b border-slate-600 text-left  bg-gray-100">
                Total Phones
              </th>
              <th className="sticky top-0 z-10 pb-3 border-b border-slate-600 text-left  bg-gray-200">
                Sold
              </th>
              <th className="sticky top-0 z-10 pb-3 border-b border-slate-600 text-left  bg-gray-100">
                Remaining
              </th>
              <th className="sticky top-0 z-10 pb-3 border-b border-slate-600 text-left  bg-gray-200">
                Sales(birr)
              </th>
              <th className="sticky top-0 z-10 pb-3 border-b border-slate-600 text-left  bg-gray-100">
                COGs(¥)
              </th>
              <th className="sticky top-0 z-10 pb-3 border-b border-slate-600 text-left  bg-gray-200">
                Gross Profit
              </th>
              <th className="sticky top-0 z-10 pb-3 border-b border-slate-600 text-left  bg-gray-100">
                Expenses
              </th>
              <th className="sticky top-0 z-10 pb-3 border-b border-slate-600 text-left  bg-gray-200">
                Lost Phones
              </th>
              <th className="sticky top-0 z-10 pb-3 border-b border-slate-600 text-left  bg-gray-100">
                Lost $
              </th>
              <th className="sticky top-0 z-10 pb-3 border-b border-slate-600 text-left  bg-gray-200">
                Net Profit
              </th>
              <th className="sticky top-0 z-10 border-b border-slate-600 text-left  bg-gray-100">
                Avg-Rate
              </th>
            </tr>
          </thead>
          <tbody className="overflow-y-auto">
            {filteredData.map((item, index) => {
              const price = Number(item.Price) || 0;
              const com1 = Number(item.com1) || 0;
              const com2 = Number(item.com2) || 0;
              const deliveryFee = Number(item.DeliveryFee) || 0;
              const grossProfit = item.Profit;
              const Lost = item.LostPhones$ * item.AverageRate;

              const totalBirr = () => {
                const rate = Number(item.rate) || 20;
                const carrier = Number(item.carrier) || 1500;
                const sum =
                  (price + com1 + com2 + deliveryFee) * rate + carrier;
                return Math.round(sum * 100) / 100;
              };

              const profit = () => {
                const profitValue = item.soldPrice - totalBirr();

                totalProfit += profitValue; // Accumulate total profit
                return Math.round(profitValue * 100) / 100;
              };

              return (
                <tr
                  key={index}
                  className="border-b border-slate-600 hover:bg-gray-50"
                >
                  <td className="p-2">{item.Month}</td>
                  <td className="p-2">{item.Count}</td>
                  <td className="p-2">{item.Sold}</td>
                  <td className="p-2">{item.Remaining}</td>
                  <td className="p-2">{item.Sales}</td>
                  <td className="p-2">{item.COGS}</td>
                  <td className="p-2">{item.GrossProfit}</td>
                  <td className="p-2">{expense}</td>
                  <td className="p-2">{item.LostPhones}</td>
                  <td className="p-2">{Lost}</td>
                  <td className="p-2">
                    {Math.round(grossProfit - expense - Lost, 2)}
                  </td>
                  <td className="p-2">{item.AverageRate}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Example;
